import React from "react";
import { Mail, Clock, Trash2, CheckCircle2 } from "lucide-react";
import LegalLayout, { Section, Bullets } from "@/components/LegalLayout";

const DELETION_EMAIL = "privacy@karyowads.com";

export default function DataDeletion() {
  return (
    <LegalLayout
      title="User Data Deletion"
      subtitle="You have the right to request deletion of the personal data KARYOW AI Ads Manager holds about you. This page explains how to make a request and what to expect."
      lastUpdated="June 1, 2026"
    >
      {/* Highlight cards */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="data-deletion-highlights">
        <div className="rounded-xl border border-[#242B30] bg-[#14181B] p-5">
          <Mail className="h-5 w-5 text-lime-400" />
          <div className="mt-3 font-mono text-[11px] tracking-[0.16em] uppercase text-zinc-500">Contact</div>
          <a
            href={`mailto:${DELETION_EMAIL}`}
            data-testid="data-deletion-email"
            className="mt-1 block text-sm font-semibold text-lime-400 hover:text-lime-300 break-all"
          >
            {DELETION_EMAIL}
          </a>
        </div>
        <div className="rounded-xl border border-[#242B30] bg-[#14181B] p-5">
          <Clock className="h-5 w-5 text-lime-400" />
          <div className="mt-3 font-mono text-[11px] tracking-[0.16em] uppercase text-zinc-500">Timeframe</div>
          <div className="mt-1 text-sm font-semibold text-white">Within 30 days</div>
        </div>
        <div className="rounded-xl border border-[#242B30] bg-[#14181B] p-5">
          <Trash2 className="h-5 w-5 text-lime-400" />
          <div className="mt-3 font-mono text-[11px] tracking-[0.16em] uppercase text-zinc-500">Scope</div>
          <div className="mt-1 text-sm font-semibold text-white">All personal & Meta data</div>
        </div>
      </div>

      <Section title="1. How to Request Deletion of Your Data">
        <p>
          To request deletion of the data associated with your KARYOW AI Ads Manager account, please choose one of
          the following methods:
        </p>
        <Bullets items={[
          <>Email us at <span className="text-lime-400">{DELETION_EMAIL}</span> from the email address associated with your account, using the subject line "Data Deletion Request".</>,
          "If you signed up or connected via Facebook/Meta, include your name and the email you used so we can locate your records.",
          "Alternatively, sign in to the platform, open Workspace & Brand settings, disconnect any linked Meta account, and email us to confirm you want your account and data permanently deleted.",
        ]} />
      </Section>

      <Section title="2. What Information to Include">
        <p>To help us verify and process your request quickly, please include:</p>
        <Bullets items={[
          "The full name on your account.",
          "The email address associated with your KARYOW account.",
          "If applicable, your connected Meta ad account ID or Facebook user reference.",
          "A clear statement that you are requesting deletion of your personal data.",
        ]} />
      </Section>

      <Section title="3. What Data Will Be Deleted">
        <p>Upon a verified request, we will delete:</p>
        <Bullets items={[
          "Your account and profile information (name, email, profile photo, workspace and brand settings).",
          "Advertising data you created, including campaigns, ad sets, ads, budgets, and manual tracking entries.",
          "Meta integration data, including stored access tokens, ad account identifiers, and synced insights.",
          "AI Ad Studio content you generated, including prompts, briefs, copy, and images.",
        ]} />
        <p>
          We may retain a limited amount of information where required for legal, tax, accounting, or security
          purposes, after which it will also be deleted or anonymized.
        </p>
      </Section>

      <Section title="4. Deletion Timeframe">
        <div className="flex items-start gap-3 rounded-xl border border-lime-400/30 bg-lime-400/5 p-5">
          <CheckCircle2 className="h-5 w-5 text-lime-400 shrink-0 mt-0.5" />
          <p className="text-sm text-zinc-300">
            We will process and complete verified data deletion requests within <span className="font-semibold text-white">30 days</span> of
            receipt. We will confirm by email once your data has been deleted. If we need additional time to comply
            with a complex request, we will notify you.
          </p>
        </div>
      </Section>

      <Section title="5. Meta / Facebook Data Deletion">
        <p>
          If you connected your Facebook or Instagram (Meta) account to KARYOW AI Ads Manager, this page also
          serves as our Data Deletion Instructions for the Facebook app integration. Submitting a request as
          described above will remove all Meta Platform data we hold about you, and you may also disconnect the
          integration at any time from within the platform.
        </p>
      </Section>

      <Section title="6. Contact">
        <p>
          For any questions about deleting your data, contact us at{" "}
          <a href={`mailto:${DELETION_EMAIL}`} className="text-lime-400 hover:text-lime-300">{DELETION_EMAIL}</a>.
        </p>
      </Section>
    </LegalLayout>
  );
}
